import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { usePlan } from '@/context/PlanContext';
import { BASE_URL } from '@/config/constants';
import axios from '../utils/axiosInstance';
import { toast } from 'sonner';
import { Check, X, Crown, Lock, AlertTriangle, Info, TicketPercent } from 'lucide-react';
import { loadRazorpayCheckout } from '@/lib/razorpayCheckout';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceType?: string;
}

type BillingCycle = 'monthly' | 'annual';
type TargetPlan = 'pro' | 'pro_max';

interface PlanChangeQuote {
  currentPeriodEnd: string;
  estimatedCreditAmount: number;
  estimatedChargeAmount: number;
  completedMonths?: number;
  monthlyCreditAmount?: number;
  annualListPriceAmount?: number;
  changeKind?: 'annual_upgrade' | 'monthly_immediate_replacement' | 'monthly_scheduled_replacement' | 'monthly_to_annual' | 'annual_to_monthly' | 'scheduled_annual_upgrade';
  destinationSku?: string;
  transition?: 'IMMEDIATE_UPGRADE' | 'IMMEDIATE_SWITCH' | 'PRORATED_UPGRADE' | 'SCHEDULED_DOWNGRADE';
  effectiveAt?: string;
  amount?: number;
  credit?: number;
  listPrice?: number;
  currentExpiry?: string | null;
  destinationExpiry?: string | null;
  explanation?: string;
}

interface PlanOption extends PlanChangeQuote {
  destinationSku: string;
  plan: TargetPlan;
  billingCycle: BillingCycle;
  price: number;
  active: boolean;
  allowed: boolean;
  code?: string;
}

const formatPaise = (amount: number) => `₹${(amount / 100).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const UpgradeModal: React.FC<UpgradeModalProps> = ({ open, onOpenChange, resourceType }) => {
  const { planInfo, isInGracePeriod, refreshPlanInfo } = usePlan();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [targetPlan, setTargetPlan] = useState<TargetPlan | null>('pro');
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [confirmingPublication, setConfirmingPublication] = useState(false);
  const [planChangeConfirmationOpen, setPlanChangeConfirmationOpen] = useState(false);
  const [planChangeQuote, setPlanChangeQuote] = useState<PlanChangeQuote | null>(null);
  const [planOptions, setPlanOptions] = useState<PlanOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const features = planInfo?.features || [
    { feature: "Themes", pro: "10 designs", proMax: "Unlimited" },
    { feature: "Promo Banners", pro: "3", proMax: "Unlimited" },
    { feature: "Tours & Events", pro: "3", proMax: "Unlimited" },
    { feature: "Showcase Songs", pro: "10", proMax: "Unlimited" },
    { feature: "Image Gallery", pro: "6 images", proMax: "Unlimited" },
    { feature: "Awards", pro: "3", proMax: "Unlimited" },
    { feature: "Layouts", pro: "Locked", proMax: "Full Access" },
    { feature: "Custom Categories", pro: "Locked", proMax: "Full Access" },
    { feature: "Custom Domain", pro: "Locked", proMax: "Full Access" },
    { feature: "Website Leads", pro: "Locked", proMax: "Full Access" },
    { feature: "Visitor Analytics", pro: "Locked", proMax: "Full Access" },
    { feature: "Invite Manager", pro: "Locked", proMax: "Full Access" },
  ];

  const priceCatalog = planInfo?.priceCatalog || {
    pro: { monthly: 99, annual: 999 },
    pro_max: { monthly: 599, annual: 4999 },
  };
  const selectedPrice = targetPlan ? priceCatalog[targetPlan][billingCycle] : 0;
  const paidBillingCycle = planInfo?.effectivePlan !== 'unpaid' ? planInfo?.billingCycle : null;
  const isMonthlyToAnnual = paidBillingCycle === 'monthly' && billingCycle === 'annual';
  const isManagePlan = resourceType === 'manage-plan';
  const currentPlan = planInfo?.effectivePlan;
  const isCurrentSelection = !!targetPlan && currentPlan === targetPlan && paidBillingCycle === billingCycle;
  const canUpgradeScheduledAnnualPro = planInfo?.pendingChange?.changeType === 'monthly_to_annual'
    && planInfo.pendingChange.status === 'migration_scheduled'
    && planInfo.pendingChange.targetPlan === 'pro';
  const isScheduledAnnualUpgradeSelection = canUpgradeScheduledAnnualPro
    && billingCycle === 'annual'
    && targetPlan === 'pro_max';
  const selectionAllowed = (() => {
    if (!targetPlan) return false;
    if (planInfo?.pendingChange || planInfo?.cancelAtCycleEnd) return isScheduledAnnualUpgradeSelection;
    if (currentPlan === 'unpaid') return true;
    if (isCurrentSelection || currentPlan === 'pro_max' && paidBillingCycle === 'annual') return false;
    if (currentPlan === 'pro' && paidBillingCycle === 'monthly') return true;
    if (currentPlan === 'pro' && paidBillingCycle === 'annual') {
      return targetPlan === 'pro_max';
    }
    if (currentPlan === 'pro_max' && paidBillingCycle === 'monthly') {
      return billingCycle === 'annual' || targetPlan === 'pro';
    }
    return false;
  })();

  useEffect(() => {
    if (!open) return;
    setPlanChangeQuote(null);
    setPlanChangeConfirmationOpen(false);
    if (resourceType === 'manage-plan' && paidBillingCycle) {
      setBillingCycle(paidBillingCycle);
      setTargetPlan(planInfo?.effectivePlan === 'pro_max' ? 'pro_max' : 'pro');
      setOptionsLoading(true);
      axios.get(`${BASE_URL}/api/billing/plan-options`)
        .then((response) => setPlanOptions(response.data?.options || []))
        .catch((error) => {
          setPlanOptions([]);
          toast.error(error.response?.data?.message || 'Plan options could not be refreshed.');
        })
        .finally(() => setOptionsLoading(false));
      return;
    }
    if (resourceType === 'annual-switch' && paidBillingCycle === 'monthly') {
      setBillingCycle('annual');
      setTargetPlan(planInfo?.effectivePlan === 'pro_max' ? 'pro_max' : 'pro');
      return;
    }
    if (paidBillingCycle) setBillingCycle(paidBillingCycle);
    // Default the selection to the user's own active plan. Never auto-select
    // the OTHER plan — the user must consciously pick it to continue.
    if (planInfo?.effectivePlan === 'pro_max') {
      setTargetPlan('pro_max');
    } else if (planInfo?.effectivePlan !== 'pro') {
      // Unpaid users get Pro pre-selected as a sensible starting point.
      setTargetPlan('pro');
    }
  }, [open, paidBillingCycle, planInfo?.effectivePlan, resourceType]);

  // When the user switches billing cycle, keep the plan selection untouched.
  // The active plan (if any) stays selected; the user consciously picks a
  // different plan if they want one. Only clear the selection when the
  // currently selected plan is not available in the new cycle.
  const handleBillingCycleChange = (nextCycle: BillingCycle) => {
    setBillingCycle(nextCycle);
  };

  // Clicking a plan card toggles selection: click to select, click again to
  // deselect. With no selection the CTA is disabled, so the user must
  // consciously pick a plan before continuing.
  const togglePlanSelection = (plan: TargetPlan) => {
    setTargetPlan((prev) => (prev === plan ? null : plan));
  };

  useEffect(() => {
    if (!confirmingPublication) return;
    if (planInfo?.isPublished) {
      setConfirmingPublication(false);
      onOpenChange(false);
      toast.success('Your website is live.');
      return;
    }

    const reconcile = async () => {
      const endpoint = billingCycle === 'monthly'
        ? '/api/subscription/reconcile'
        : '/api/payment/reconcile-publication';
      try {
        const response = await axios.post(`${BASE_URL}${endpoint}`);
        if (response.data?.isPublished) {
          setConfirmingPublication(false);
          onOpenChange(false);
          toast.success('Your website is live.');
          return;
        }
      } catch (error: any) {
        const retryableCodes = new Set([
          'SUBSCRIPTION_NOT_ACTIVE',
          'ACTIVE_ENTITLEMENT_REQUIRED',
          'PUBLICATION_RETRY_FAILED',
        ]);
        if (!retryableCodes.has(error.response?.data?.code)) {
          toast.error(error.response?.data?.message || 'Publication confirmation needs attention.');
          setConfirmingPublication(false);
          return;
        }
      }
      refreshPlanInfo();
    };
    reconcile();
    const intervalId = window.setInterval(refreshPlanInfo, 3000);
    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
      setConfirmingPublication(false);
      toast.info('Your entitlement is preserved. Publication will continue retrying safely.');
    }, 60000);
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [billingCycle, confirmingPublication, onOpenChange, planInfo?.isPublished, refreshPlanInfo]);

  const resourceLabels: Record<string, string> = {
    showcases: 'Showcase Songs',
    images: 'Image Gallery',
    banners: 'Promo Banners',
    toursEvents: 'Tours & Events',
    awards: 'Awards',
    themes: 'Themes',
    categories: 'Custom Categories',
    contactLeadsAccess: 'Website Leads',
    visitorAnalyticsAccess: 'Visitor Analytics',
    customDomain: 'Custom Domain',
  };

  const lockedTooltips: Record<string, string> = {
    'Custom Domain': 'Connect your own domain for a professional web presence.',
    'Custom Categories': 'Create and manage unlimited custom categories.',
    'Website Leads': 'Receive and manage leads directly from your website.',
    'Visitor Analytics': 'Track daily visitor trends, sessions, and unique visitors for your website.',
  };

  const startMonthlyReplacement = async () => {
    await loadRazorpayCheckout();
    const destinationSku = `${targetPlan === 'pro_max' ? 'pro_max' : 'pro'}_monthly`;
    const replacement = await axios.post(`${BASE_URL}/api/subscription/replacement/create`, { targetPlan, destinationSku });
    const { subscriptionId, keyId } = replacement.data;
    if (['completed', 'migration_scheduled'].includes(replacement.data.status)) {
      await refreshPlanInfo();
      toast.success(replacement.data.message || (targetPlan === 'pro' ? 'Your switch to Pro is scheduled.' : 'Pro Max is active.'));
      onOpenChange(false);
      return;
    }
    if (!subscriptionId || !keyId) {
      throw new Error('The Pro Max subscription checkout response is incomplete.');
    }
    const checkout = new window.Razorpay({
      key: keyId,
      subscription_id: subscriptionId,
      name: 'Artist Intro',
      description: targetPlan === 'pro'
        ? `Authorize Pro Monthly from ${replacement.data.startsAt ? new Date(replacement.data.startsAt).toLocaleDateString('en-IN') : 'your next renewal'}`
        : 'Pro Max Monthly — ₹599 charged now',
      handler: async (payment: any) => {
        try {
          const result = await axios.post(`${BASE_URL}/api/subscription/replacement/authorize`, {
            razorpay_payment_id: payment.razorpay_payment_id,
            razorpay_subscription_id: payment.razorpay_subscription_id,
            razorpay_signature: payment.razorpay_signature,
          });
          await refreshPlanInfo();
          if (['completed', 'migration_scheduled'].includes(result.data.status)) {
            toast.success(result.data.message || (targetPlan === 'pro'
              ? 'Your switch to Pro is scheduled.'
              : 'Pro Max is active. Your new monthly billing cycle starts today.'));
            onOpenChange(false);
            return;
          }
          toast.info(targetPlan === 'pro'
            ? 'The Pro renewal authorization is being confirmed.'
            : 'Your ₹599 payment is being confirmed. Your current plan remains active until the handoff completes.');
          onOpenChange(true);
          setConfirmingPublication(true);
        } catch (error: any) {
          toast.error(error.response?.data?.message || 'The plan change could not be confirmed. Your current plan remains active.');
          onOpenChange(true);
        }
      },
      theme: { color: '#0b47d9' },
      modal: { ondismiss: () => onOpenChange(true) },
    });
    onOpenChange(false);
    checkout.open();
  };

  const handleUpgrade = async (confirmedPlanChange = false) => {
    setLoading(true);
    try {
      if (isManagePlan && !confirmedPlanChange) {
        const destinationSku = `${targetPlan === 'pro_max' ? 'pro_max' : 'pro'}_${billingCycle}`;
        const quoteResponse = await axios.get(`${BASE_URL}/api/billing/plan-change-quote`, {
          params: { destinationSku },
        });
        setPlanChangeQuote(quoteResponse.data);
        setPlanChangeConfirmationOpen(true);
        return;
      }
      const entitlementAlreadyCoversSelection = !isManagePlan && !isMonthlyToAnnual && (
        planInfo?.effectivePlan === targetPlan || planInfo?.effectivePlan === 'pro_max'
      );
      if (entitlementAlreadyCoversSelection) {
        setConfirmingPublication(true);
        return;
      }

      if (billingCycle === 'monthly') {
        const isMonthlyUpgrade = paidBillingCycle === 'monthly'
          && planInfo?.effectivePlan === 'pro'
          && targetPlan === 'pro_max';
        const isMonthlyDowngrade = paidBillingCycle === 'monthly'
          && planInfo?.effectivePlan === 'pro_max'
          && targetPlan === 'pro';
        const isAnnualToMonthly = paidBillingCycle === 'annual'
          && planInfo?.effectivePlan === 'pro'
          && !!targetPlan && ['pro', 'pro_max'].includes(targetPlan);
        if (isMonthlyUpgrade || isMonthlyDowngrade || isAnnualToMonthly) {
          if (!confirmedPlanChange) {
            setPlanChangeQuote({
              currentPeriodEnd: planInfo.planExpiresAt || '',
              estimatedCreditAmount: 0,
              estimatedChargeAmount: priceCatalog[targetPlan!].monthly * 100,
              changeKind: isMonthlyDowngrade
                ? 'monthly_scheduled_replacement'
                : isAnnualToMonthly ? 'annual_to_monthly' : 'monthly_immediate_replacement',
            });
            setPlanChangeConfirmationOpen(true);
            return;
          }
          await startMonthlyReplacement();
          return;
        }

        await loadRazorpayCheckout();
        // ── Monthly subscription (AutoPay) flow ──
        // Creates a Razorpay recurring subscription; the plan is activated via
        // the backend webhook on `subscription.activated`/`subscription.charged`.
        const subResponse = await axios.post(`${BASE_URL}/api/subscription/create`, { targetPlan });
        const { subscriptionId, keyId, domainSlug: subDomainName } = subResponse.data;
        if (!subscriptionId || !keyId || !subDomainName) {
          throw new Error('The subscription checkout response is incomplete.');
        }

        const options = {
          key: keyId,
          subscription_id: subscriptionId,
          name: 'Artist Intro',
          description: `${targetPlan === 'pro' ? 'Pro' : 'Pro Max'} Monthly for ${subDomainName}`,
          handler: async function (response: any) {
            try {
              const verification = await axios.post(`${BASE_URL}/api/subscription/verify`, {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_signature: response.razorpay_signature,
              });
              refreshPlanInfo();
              if (verification.data?.isPublished) {
                toast.success('Payment verified. Your website is live.');
                onOpenChange(false);
                return;
              }
              toast.info('Payment verified. Publication prerequisites still need attention.');
            } catch (error: any) {
              console.error('Subscription verification error:', error);
              toast.info('Payment received. Confirming subscription and publication...');
            }
            onOpenChange(true);
            setConfirmingPublication(true);
          },
          prefill: { name: '', email: '' },
          theme: { color: '#0b47d9' },
          modal: {
            ondismiss: function() {
              setLoading(false);
              onOpenChange(true);
            }
          }
        };

        const razorpay = new window.Razorpay(options);
        onOpenChange(false);
        razorpay.open();
        return;
      }

      if (isMonthlyToAnnual && !confirmedPlanChange) {
        if (isScheduledAnnualUpgradeSelection) {
          setPlanChangeQuote({
            currentPeriodEnd: planInfo?.pendingChange?.currentPeriodEnd || planInfo?.planExpiresAt || '',
            estimatedCreditAmount: 99900,
            estimatedChargeAmount: 400000,
            annualListPriceAmount: 499900,
            changeKind: 'scheduled_annual_upgrade',
          });
          setPlanChangeConfirmationOpen(true);
          return;
        }
        setPlanChangeQuote({
          currentPeriodEnd: planInfo?.planExpiresAt || '',
          estimatedCreditAmount: 0,
          estimatedChargeAmount: selectedPrice * 100,
          changeKind: 'monthly_to_annual',
        });
        setPlanChangeConfirmationOpen(true);
        return;
      }

      if (planInfo?.effectivePlan === 'pro' && targetPlan === 'pro_max' && !confirmedPlanChange) {
        const quoteResponse = await axios.get(`${BASE_URL}/api/payment/annual-upgrade-quote`, {
          params: { targetPlan },
        });
        setPlanChangeQuote(quoteResponse.data);
        setPlanChangeConfirmationOpen(true);
        return;
      }

      await loadRazorpayCheckout();
      // ── Annual one-time payment flow ──
      // Create order — pass billingCycle so backend computes the right amount
      const destinationSku = `${targetPlan === 'pro_max' ? 'pro_max' : 'pro'}_${billingCycle}`;
      const orderResponse = await axios.post(`${BASE_URL}/api/payment/create-order`, { billingCycle, targetPlan, destinationSku });
      const { orderId, amount, currency, keyId, domainName } = orderResponse.data;
      if (!orderId || !Number.isFinite(amount) || !currency || !keyId || !domainName) {
        throw new Error('The payment checkout response is incomplete.');
      }

      // Open Razorpay checkout
      const options = {
        key: keyId,
        amount: amount,
        currency: currency,
        name: 'Artist Intro',
        description: `${targetPlan === 'pro' ? 'Pro' : 'Pro Max'} Annual for ${domainName}`,
        order_id: orderId,
        handler: async function (response: any) {
          try {
            // Verify payment — pass billingCycle so backend sets correct expiry
            const verification = await axios.post(`${BASE_URL}/api/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verification.data?.scheduled) {
              toast.success(verification.data.message || 'Annual plan purchased and scheduled.');
              refreshPlanInfo();
              onOpenChange(false);
              return;
            }
            if (verification.data?.paymentCaptured) {
              toast.info(verification.data.message || 'Annual payment received. Confirming the scheduled switch.');
              refreshPlanInfo();
              onOpenChange(false);
              return;
            }

            toast.success('Payment verified. Publishing your website...');
            onOpenChange(true);
            setConfirmingPublication(true);
            refreshPlanInfo();
          } catch {
            toast.error('Payment verification failed. Please contact support.');
            onOpenChange(true);
          }
        },
        prefill: { name: '', email: '' },
        theme: { color: '#0b47d9' },
        modal: {
          ondismiss: function() {
            setLoading(false);
            onOpenChange(true);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      // Close the dialog only once checkout is ready to open.
      onOpenChange(false);
      razorpay.open();
    } catch (err: any) {
      console.error('Error initiating payment:', err);
      if (err.response?.status === 429) {
        toast.error('Too many attempts. Please wait a few minutes before trying again.');
      } else {
        toast.error(err.response?.data?.message || err.message || 'Failed to initiate payment');
      }
      onOpenChange(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePromoRedeem = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/promo-codes/redeem`, { code: promoCode.trim() });
      toast.success(response.data?.isPublished ? 'Promo applied. Your website is live.' : 'Promo applied. Publication is being confirmed.');
      setPromoCode('');
      if (response.data?.isPublished) {
        onOpenChange(false);
      } else {
        setConfirmingPublication(true);
      }
      refreshPlanInfo();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Promo code is invalid or unavailable');
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl lg:max-w-3xl h-[96vh] flex flex-col p-0 overflow-hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} showCloseButton={false}>
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <div className="flex items-center gap-2">
            <Crown className="w-6 h-6 text-primary" />
            <DialogTitle className="text-xl">
              {resourceType === 'publish' ? 'Publish Your Website' : isManagePlan ? 'Manage Plan' : 'Choose Your Plan'}
            </DialogTitle>
          </div>
          <DialogDescription>
            {resourceType && !['publish', 'manage-plan', 'annual-switch'].includes(resourceType) ? (
              <span className="flex items-center gap-2 mt-2 text-primary">
                <Lock className="w-4 h-4" />
                You've reached the limit for {resourceLabels[resourceType] || resourceType}. Upgrade to unlock unlimited access.
              </span>
            ) : (
              isManagePlan
                ? 'Review your active plan and choose from the available changes.'
                : 'Choose a plan to go live. Your website publishes after payment is confirmed.'
            )}
          </DialogDescription>
        </DialogHeader>

        {/* ─── Section 1: Heading, cycle toggle, pricing cards (sticky top) ─── */}
        <div className="shrink-0 px-6 pt-0 pb-2 bg-card z-10">
          {isInGracePeriod && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm text-amber-800 mb-4">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm">
                Your plan has expired. You have {planInfo?.gracePeriodDaysRemaining} days remaining in your grace period.
              </span>
            </div>
          )}

          {confirmingPublication && (
            <div className="mb-5 flex items-center gap-3 rounded-sm border border-primary/30 bg-primary/5 p-4 text-sm">
              <Spinner size="sm" />
              <div>
                <p className="font-medium text-foreground">Confirming payment and publication</p>
                <p className="text-muted-foreground">Keep this window open. Retrying will not charge you again.</p>
              </div>
            </div>
          )}

          {/* Billing Cycle Toggle */}
          <div className="flex flex-col items-center gap-2 mb-5">
            <Tabs
              value={billingCycle}
              onValueChange={(value) => {
                handleBillingCycleChange(value as BillingCycle);
              }}
            >
              <TabsList>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="annual" className="flex items-center gap-1.5">
                  Annual
                  <span className="text-[10px] font-bold text-white bg-primary px-1.5 py-1.5 rounded-lg leading-none">
                    {Math.round((1 - priceCatalog[targetPlan || 'pro'].annual / (priceCatalog[targetPlan || 'pro'].monthly * 12)) * 100)}% off
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {billingCycle === 'annual'}
          </div>

          <div className="mb-5" />

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
            {(['pro', 'pro_max'] as TargetPlan[]).map((plan) => {
              const selected = targetPlan === plan;
              const price = priceCatalog[plan][billingCycle];
              const matchingServerOption = isManagePlan
                ? planOptions.find((option) => option.plan === plan && option.billingCycle === billingCycle)
                : null;
              const active = matchingServerOption?.active ?? (currentPlan === plan && paidBillingCycle === billingCycle);
              const scheduled = billingCycle === 'annual'
                && planInfo?.pendingChange?.changeType === 'monthly_to_annual'
                && planInfo.pendingChange.status === 'migration_scheduled'
                && planInfo.pendingChange.targetPlan === plan;
              const serverOption = matchingServerOption;
              const cardAllowed = serverOption ? serverOption.allowed : (() => {
                if (planInfo?.pendingChange || planInfo?.cancelAtCycleEnd) {
                  return canUpgradeScheduledAnnualPro && billingCycle === 'annual' && plan === 'pro_max';
                }
                if (currentPlan === 'unpaid') return true;
                if (active || currentPlan === 'pro_max' && paidBillingCycle === 'annual') return false;
                if (currentPlan === 'pro' && paidBillingCycle === 'monthly') return true;
                if (currentPlan === 'pro' && paidBillingCycle === 'annual') return plan === 'pro_max';
                if (currentPlan === 'pro_max' && paidBillingCycle === 'monthly') {
                  return billingCycle === 'annual' || plan === 'pro';
                }
                return false;
              })();
              const actionLabel = serverOption?.active
                ? 'ACTIVE'
                : serverOption?.transition === 'SCHEDULED_DOWNGRADE'
                  ? 'DOWNGRADE'
                  : serverOption?.transition === 'IMMEDIATE_SWITCH' && plan === 'pro'
                    ? 'DOWNGRADE'
                    : serverOption?.allowed ? 'UPGRADE' : 'UNAVAILABLE';
              return (
                <button
                  key={plan}
                  type="button"
                  onClick={() => togglePlanSelection(plan)}
                  disabled={confirmingPublication || optionsLoading || !cardAllowed}
                  className={`relative p-4 border-2 rounded-lg text-left transition-colors ${active ? 'border-success' : !cardAllowed ? 'border-border opacity-50 cursor-not-allowed' : selected ? 'border-primary' : 'border-border hover:border-primary/50'}`}
                >
                  {active && (
                    <span className="absolute top-2 right-2 px-2 py-1.5 bg-success text-white text-xs font-medium rounded-lg">Active</span>
                  )}
                  {scheduled && (
                    <span className="absolute top-2 right-2 px-2 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg">Scheduled</span>
                  )}
                  {selected && !active && (
                    <span className="absolute top-2 right-2 flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  )}
                  <h3 className="font-semibold text-primary mb-2 flex items-center gap-1">
                    {/* {plan === 'pro_max' ? <Crown className="w-4 h-4" /> : <Zap className="w-4 h-4" />} */}
                    {plan === 'pro_max' ? 'Pro Max' : 'Pro'}
                  </h3>
                  <div className="text-2xl font-bold">
                    ₹{price.toLocaleString('en-IN')}
                    <span className="text-sm text-muted-foreground">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                    {billingCycle === 'annual' && (
                      <span className="ml-2 text-lg font-normal text-muted-foreground line-through">
                        ₹{(priceCatalog[plan].monthly * 12).toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>
                  {isManagePlan && (
                    <p className={`mt-3 text-xs font-semibold ${serverOption?.active ? 'text-green-700' : cardAllowed ? 'text-primary' : 'text-muted-foreground'}`}>
                      {actionLabel}
                    </p>
                  )}
                  <p className="mt-3 text-xs text-muted-foreground">
                    {plan === 'pro_max' ? 'Everything you need to customize, grow and make an impact.' : 'Everything you need to get your artist website live.'}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── Section 2: Feature comparison (scrollable middle) ─── */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-2">
          <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 rounded-t-lg">
                  <th className="text-left p-3 font-medium first:rounded-tl-lg">Feature</th>
                  <th className="text-center p-3 font-medium w-28">Pro</th>
                  <th className="text-center p-3 font-medium w-28 last:rounded-tr-lg">Pro Max</th>
                </tr>
              </thead>
              <tbody>
                {features.map((item, index) => (
                  <tr
                    key={index}
                    className={`border-t border-border ${
                      (resourceType && resourceLabels[resourceType] === item.feature) || item.pro === 'Locked'
                        ? 'bg-primary-light'
                        : ''
                    }`}
                  >
                    <td className="p-3 text-sm font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        {item.feature}
                        {(resourceType && resourceLabels[resourceType] === item.feature || item.pro === 'Locked') && (
                          <Lock className="inline w-4 h-4 text-primary" />
                        )}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {item.pro === 'Locked' ? (
                        <X className="w-5 h-5 text-red-500 mx-auto" />
                      ) : (
                        <span className="text-sm text-muted-foreground">{item.pro}</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {item.proMax === 'Unlimited' || item.proMax === 'Full Access' ? (
                        <span className="relative inline-flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-500" />
                          {item.pro === 'Locked' && lockedTooltips[item.feature] && (
                            <span className="absolute right-0 translate-x-full pl-1 group">
                              <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                              <span className="absolute bottom-full right-0 mb-2 px-3 py-1.5 text-sm font-normal text-white bg-foreground rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                                {lockedTooltips[item.feature]}
                              </span>
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-primary">{item.proMax}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ─── Section 3: Promo code + CTA buttons (sticky bottom) ─── */}
        <div className="shrink-0 px-6 pb-6 pt-2 bg-card z-10 space-y-4">
          <div className="flex flex-col sm:flex-row gap-2 rounded-sm border border-border p-3">
            <div className="flex items-center gap-2 text-sm font-medium sm:w-32">
              <TicketPercent className="w-4 h-4 text-primary" /> Access code
            </div>
            <Input value={promoCode} onChange={(event) => setPromoCode(event.target.value.toUpperCase())} placeholder="AI-..." autoComplete="off" disabled={confirmingPublication} />
            <Button type="button" variant="outline" onClick={handlePromoRedeem} disabled={confirmingPublication || promoLoading || !promoCode.trim()}>
              {promoLoading ? <Spinner size="sm" /> : 'Apply'}
            </Button>
          </div>

          {isManagePlan && planInfo?.pendingChange && (
            <div className="border-l-2 border-primary pl-3 text-sm">
              <p className="font-medium">Plan change in progress</p>
              <p className="text-muted-foreground">Finish or wait for the current billing change before choosing another plan.</p>
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              {isManagePlan ? 'Close' : 'Maybe Later'}
            </Button>
            <Button
              onClick={() => handleUpgrade(false)}
              disabled={loading || confirmingPublication || optionsLoading || (isManagePlan
                ? !planOptions.some((option) => option.plan === targetPlan && option.billingCycle === billingCycle && option.allowed)
                : !selectionAllowed)}
              className="flex-1"
            >
              {loading || confirmingPublication ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" className="text-inherit" />
                  {confirmingPublication ? 'Confirming...' : 'Processing...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {/* <Zap className="w-4 h-4" /> */}
                  {!targetPlan
                    ? 'Select a plan to continue'
                    : isCurrentSelection
                    ? 'Active Plan'
                    : currentPlan === 'pro_max' && paidBillingCycle === 'annual'
                      ? 'No plan changes available'
                    : isScheduledAnnualUpgradeSelection
                      ? 'Upgrade Scheduled Plan — ₹4,000'
                    : billingCycle === 'monthly'
                    ? `Subscribe & Publish`
                    : isMonthlyToAnnual
                      ? 'Upgrade to Annual'
                      : `Pay & Publish`}
                </span>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <ConfirmDialog
      open={planChangeConfirmationOpen}
      onOpenChange={setPlanChangeConfirmationOpen}
      title={isManagePlan && planChangeQuote?.transition
        ? `${planChangeQuote.transition === 'SCHEDULED_DOWNGRADE' ? 'Schedule' : planChangeQuote.transition === 'IMMEDIATE_SWITCH' ? 'Switch to' : 'Upgrade to'} ${targetPlan === 'pro_max' ? 'Pro Max' : 'Pro'} ${billingCycle === 'annual' ? 'Annual' : 'Monthly'}?`
        : planChangeQuote?.changeKind === 'monthly_to_annual'
        ? `Switch to ${targetPlan === 'pro' ? 'Pro' : 'Pro Max'} Annual?`
        : planChangeQuote?.changeKind === 'scheduled_annual_upgrade'
          ? 'Upgrade scheduled plan to Pro Max Annual?'
        : planChangeQuote?.changeKind === 'monthly_scheduled_replacement'
          ? 'Schedule Pro Monthly?'
          : planChangeQuote?.changeKind === 'annual_to_monthly'
            ? 'Switch to Pro Max Monthly?'
            : 'Upgrade this subscription to Pro Max?'}
      description={isManagePlan && planChangeQuote?.explanation
        ? planChangeQuote.explanation
        : planChangeQuote?.changeKind === 'monthly_to_annual'
        ? `The full annual price is charged now. ${currentPlan === 'pro_max' ? 'Pro Max' : 'Pro'} remains active until the current monthly period ends.`
        : planChangeQuote?.changeKind === 'scheduled_annual_upgrade'
          ? 'Your paid ₹999 Annual Pro purchase is credited. Only the ₹4,000 difference is charged.'
        : planChangeQuote?.changeKind === 'monthly_scheduled_replacement'
          ? 'Authorize the ₹99 mandate now. Pro Max remains active until the current monthly period ends.'
          : planChangeQuote?.changeKind === 'annual_to_monthly'
            ? 'The full ₹599 monthly subscription starts immediately. Unused Annual Pro time is forfeited.'
            : billingCycle === 'monthly'
              ? 'A new Pro Max subscription replaces your current Pro subscription after payment is confirmed.'
              : 'Completed Pro months are credited and the original annual expiry is preserved.'}
      body={(
        <div className="space-y-2">
          {isManagePlan && planChangeQuote?.transition ? (
            <>
              <p>{targetPlan === 'pro_max' ? 'Pro Max' : 'Pro'} {billingCycle === 'annual' ? 'Annual' : 'Monthly'}: {formatPaise(planChangeQuote.listPrice || planChangeQuote.amount || 0)}</p>
              {(planChangeQuote.credit || 0) > 0 && (
                <p>Completed-month credit: −{formatPaise(planChangeQuote.credit || 0)}</p>
              )}
              <p className="font-medium">Amount payable: {formatPaise(planChangeQuote.amount || 0)}</p>
              {planChangeQuote.transition === 'PRORATED_UPGRADE' && (
                <>
                  <p>Completed Pro months: {planChangeQuote.completedMonths || 0}</p>
                  <p>Current annual access remains valid until {planChangeQuote.currentExpiry ? new Date(planChangeQuote.currentExpiry).toLocaleDateString('en-IN') : 'the existing expiry'}.</p>
                </>
              )}
              {planChangeQuote.transition === 'SCHEDULED_DOWNGRADE' && (
                <p>Your new plan starts on {planChangeQuote.effectiveAt ? new Date(planChangeQuote.effectiveAt).toLocaleDateString('en-IN') : 'the next monthly boundary'}.</p>
              )}
            </>
          ) : planChangeQuote?.changeKind === 'monthly_to_annual' ? (
            <>
              <p>Charge now: ₹{selectedPrice.toLocaleString('en-IN')}.</p>
              <p>There is no credit or refund for the remaining monthly period.</p>
              <p>Your annual plan starts on {planChangeQuote.currentPeriodEnd ? new Date(planChangeQuote.currentPeriodEnd).toLocaleDateString('en-IN') : 'your monthly renewal date'} and runs for one year.</p>
            </>
          ) : planChangeQuote?.changeKind === 'scheduled_annual_upgrade' ? (
            <>
              <p>Pro Max Annual price: ₹4,999.00.</p>
              <p>Credit from scheduled Annual Pro: ₹999.00.</p>
              <p className="font-medium">Charge now: ₹4,000.00.</p>
              <p>Pro Max Annual starts on {planChangeQuote.currentPeriodEnd ? new Date(planChangeQuote.currentPeriodEnd).toLocaleDateString('en-IN') : 'the existing scheduled date'} and runs for one year.</p>
            </>
          ) : planChangeQuote?.changeKind === 'monthly_scheduled_replacement' ? (
            <>
              <p>Authorize the ₹99 Pro mandate now.</p>
              <p>The first ₹99 charge occurs on {planChangeQuote.currentPeriodEnd ? new Date(planChangeQuote.currentPeriodEnd).toLocaleDateString('en-IN') : 'your next renewal date'}.</p>
              <p>Pro Max remains active until that payment activates Pro.</p>
            </>
          ) : planChangeQuote?.changeKind === 'annual_to_monthly' ? (
            <>
              <p>Charge now: ₹599.00.</p>
              <p>Your Annual Pro entitlement ends when the ₹599 payment is confirmed.</p>
              <p>There is no credit or refund for unused Annual Pro time. Pro Max Monthly starts immediately with a new renewal date.</p>
            </>
          ) : billingCycle === 'monthly' ? (
            <>
              <p>Charge now: ₹599.00</p>
              <p>There is no credit or refund for unused time on the current ₹99 Pro month.</p>
              <p>Your new monthly renewal date starts when the ₹599 payment is confirmed. Pro remains active if the handoff fails.</p>
            </>
          ) : (
            <>
              <p>Pro Max Annual price: ₹{((planChangeQuote?.annualListPriceAmount || 499900) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
              <p>Completed Pro months: {planChangeQuote?.completedMonths || 0}</p>
              <p>Credit: {planChangeQuote?.completedMonths || 0} × ₹{((planChangeQuote?.monthlyCreditAmount || 8325) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })} = ₹{((planChangeQuote?.estimatedCreditAmount || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
              <p className="font-medium">Charge now: ₹{((planChangeQuote?.estimatedChargeAmount || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
              <p>The current partial month receives no credit. Your annual expiry remains {planChangeQuote?.currentPeriodEnd ? new Date(planChangeQuote.currentPeriodEnd).toLocaleDateString('en-IN') : 'unchanged'}.</p>
            </>
          )}
        </div>
      )}
      confirmLabel={isManagePlan && planChangeQuote?.transition
        ? `${planChangeQuote.transition === 'SCHEDULED_DOWNGRADE' ? 'Confirm' : 'Pay'} ${formatPaise(planChangeQuote.amount || 0)}`
        : planChangeQuote?.changeKind === 'monthly_to_annual'
        ? `Pay ₹${selectedPrice.toLocaleString('en-IN')}`
        : planChangeQuote?.changeKind === 'scheduled_annual_upgrade'
          ? 'Pay ₹4,000 and upgrade'
        : planChangeQuote?.changeKind === 'monthly_scheduled_replacement'
          ? 'Authorize ₹99 renewal'
          : planChangeQuote?.changeKind === 'annual_to_monthly'
            ? 'Pay ₹599 and switch'
            : billingCycle === 'monthly' ? 'Pay ₹599 and upgrade' : 'Confirm prorated upgrade'}
      cancelLabel="Not now"
      variant="default"
      onConfirm={async () => {
        setPlanChangeConfirmationOpen(false);
        await handleUpgrade(true);
      }}
    />
    </>
  );
};

export default UpgradeModal;
